import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { isEmpty, filter, map } from 'lodash';
import { v4 as uuid } from 'uuid';
import clsx from 'clsx';

import Skeleton from '../utils/skeleton';
import { iconCheck, iconArrow } from './defaults';
import useWindowSize from '../../../hooks/useWindowSize';

const Select = ({
  id,
  className,
  label,
  items,
  selected,
  onClick,
  notResultText,
  loading,
  disabled,
  hiddenSelected,
  hideItemsWhenInactive,
  filterable,
  filterType,
  hasError,
  hasSpan,
  sufixHolder,
  defaultItemsView,
  defaultViewMoreItems,
  newRenderMobile,
}) => {
  const { width } = useWindowSize();

  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(disabled || false);
  const [inputValue, setInputValue] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const inputEl = useRef(null);
  const breakpoint = 1024;
  const isMobile = width <= breakpoint;

  const filteredItems = useMemo(
    () => {
      if (hideItemsWhenInactive && !open) {
        return [];
      }
      if (filterable) {
        return filter(items, (row) => {
          if (isEmpty(inputValue)) return true;
          if (['any-coincidence'].includes(filterType)) {
            return new RegExp(inputValue, 'i').test(row.text);
          }
          return new RegExp(`^${inputValue}`, 'i').test(row.text);
        });
      }
      return items ?? [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, filterable, filterType, inputValue, hideItemsWhenInactive, open]
  );
  const refDropdown = useRef(null);
  const rowHeight = 56;
  let height =
    filteredItems?.length > defaultItemsView - (!hiddenSelected ? 0 : 1)
      ? !hiddenSelected
        ? rowHeight * (defaultItemsView + defaultViewMoreItems)
        : rowHeight * filteredItems?.length + 1
      : (filteredItems?.length + 1 - (!hiddenSelected ? 0 : 1)) * rowHeight;

  height =
    filterable && isEmpty(filteredItems)
      ? rowHeight * (defaultItemsView - 1)
      : height;

  useEffect(() => {
    setEnabled(!enabled);
  }, [disabled]);

  useEffect(() => {
    if (!isEmpty(selected?.text)) {
      setPlaceholder((selected.text ?? '').toString().trim());
    }
    if (selected === null && filterable) {
      setPlaceholder('');
    }
  }, [selected]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    if (!(isMobile && newRenderMobile)) {
      document.addEventListener('mousedown', handleMouseDown);
      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, []);

  const handleChange = (row) => {
    if (!disabled) {
      onClick(row);
      handleOutsideClick();
    }
  };

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const onToggle = () => {
    if (!open) {
      handleOpen();

      if (filterable) {
        inputEl.current.focus();

        if (selected) {
          setInputValue('');
          setPlaceholder(selected.text);
        }
      }
    } else {
      setOpen(false);

      if (filterable && selected) {
        setInputValue('');
        inputEl.current.blur();
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.keyCode === 27) {
      handleOutsideClick();
    }
  };

  const handleMouseDown = (event) => {
    if (
      refDropdown &&
      refDropdown.current.contains(event.target) &&
      refDropdown.current === event.target.closest('.rd--select')
    ) {
      return;
    }
    handleOutsideClick();
  };

  const handleOutsideClick = () => {
    setInputValue('');
    setOpen(false);
  };

  const handleInputChange = (e) => {
    handleOpen();
    setInputValue(e.target.value ?? '');
  };

  const getHeightShadow = () => {
    if (loading) {
      return '280px';
    }
    if (open) {
      return filterable && isEmpty(filteredItems) ? '168px' : height;
    }
    return 'initial';
  };

  const renderMobile = () => {
    return ReactDOM.createPortal(
      <div className="rd--select__mobile">
        <div className="rd--select__overlay" onClick={handleOutsideClick}></div>
        <div className="rd--select__content">
          <div className="rd--select__header">
            <div className="rd--select__header-title">{label}</div>
            <div
              className="rd--select__header-close"
              onClick={handleOutsideClick}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="11.5" stroke="#A3ABCC" />
                <path
                  d="M8.25 8.25L15.7497 15.7497"
                  stroke="#A3ABCC"
                  strokeLinecap="round"
                />
                <path
                  d="M15.75 8.25L8.25031 15.7497"
                  stroke="#A3ABCC"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <div className="rd--select__separator"></div>
          {renderList()}
        </div>
      </div>,
      document.body
    );
  };

  const renderList = () => {
    return loading ? (
      <div className="rd--select-list">
        {map(['240px', '176px', '240px', '176px'], (width, index) => (
          <div className="rd--select-list-item" key={index}>
            <Skeleton width={width} height="20px" />
          </div>
        ))}
      </div>
    ) : (
      <div
        className={clsx('rd--select-list', {
          'has-scroll': filteredItems.length > 3,
          'rd--select-list-item--not-results':
            filterable && isEmpty(filteredItems),
        })}
        style={{ maxHeight: height - rowHeight }}
      >
        {map(filteredItems, (item) => (
          <div
            key={uuid()}
            className={clsx('rd--select-list-item', {
              selected: item.value === selected?.value,
            })}
            onClick={() => handleChange(item)}
          >
            {item?.render ?? item.text}
            {item.value === selected?.value && (
              <div className="rd--select-check">{iconCheck}</div>
            )}
          </div>
        ))}
        {filterable && isEmpty(filteredItems) && (
          <div className="rd--select-list-item not-results">
            {notResultText}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        id={id}
        ref={refDropdown}
        className={clsx('rd--select', className, {
          'has-span': hasSpan,
          'has-error': hasError,
          opened: open,
          'hidden-selected': hiddenSelected,
          'hasnt-label': !label,
          filterable,
          disabled,
          'has-value':
            !isEmpty(selected) || (filterable && !isEmpty(inputValue)),
        })}
        tabIndex={0}
      >
        <div className="rd--select-input" onClick={onToggle}>
          <div className="rd--select-content">
            {!isEmpty(label) && <label>{label}</label>}
            {!filterable ? (
              <span>{selected?.text ?? ''}</span>
            ) : (
              <input
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                ref={inputEl}
              />
            )}
          </div>
          {sufixHolder && (
            <span className="rd--select--sufix">{sufixHolder}</span>
          )}
          <div className="rd--select-arrow">{iconArrow}</div>
        </div>
        {open
          ? isMobile && newRenderMobile
            ? renderMobile()
            : renderList()
          : ''}
        <div
          className="rd--select-shadow"
          style={{ height: getHeightShadow() }}
        />
      </div>
      {hasError && (
        <span className="rd--select-error" htmlFor={id}>
          {hasError}
        </span>
      )}
      {hasSpan && (
        <span className="rd--select-span" htmlFor={id}>
          {hasSpan}
        </span>
      )}
    </>
  );
};

Select.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      text: PropTypes.string.isRequired,
      group: PropTypes.string,
      render: PropTypes.node,
    }).isRequired
  ).isRequired,
  selected: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
    group: PropTypes.string,
    render: PropTypes.node,
  }),
  onClick: PropTypes.func.isRequired,
  notResultText: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  hiddenSelected: PropTypes.bool,
  /**
   * Si es `true` elimina los items del DOM cuando el select está inactivo.
   * Es recomendable su uso  para optimizar performance si hay muchos `items`.
   */
  hideItemsWhenInactive: PropTypes.bool,
  filterable: PropTypes.bool,
  newRenderMobile: PropTypes.bool,
  filterType: PropTypes.oneOf(['any-coincidence']),
  hasError: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  hasSpan: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  sufixHolder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  defaultItemsView: PropTypes.number,
  defaultViewMoreItems: PropTypes.number,
};

Select.defaultProps = {
  id: uuid(),
  className: null,
  label: null,
  selected: null,
  onClick: () => {},
  notResultText: 'No se encontraron resultados',
  loading: false,
  disabled: false,
  hiddenSelected: false,
  hideItemsWhenInactive: false,
  filterable: false,
  newRenderMobile: false,
  filterType: null,
  hasError: null,
  hasSpan: null,
  sufixHolder: null,
  defaultItemsView: 3,
  defaultViewMoreItems: 1.5,
};

export default React.memo(Select);
